// A2F-764: full-page faux Word document for the /pro-v2 right pane.
//
// Renders the active doc-type sample with every formatting knob bound:
//   H1, H2 (×2), body paragraphs (×2), bulleted list, figure caption,
//   table caption, table styled by the active TableStylePreset.
//
// The data-pv attributes mirror configurator-preview.jsx in the design
// export — they're hooks for the focus map (clicking a configurator
// section can scroll the preview to the matching block). v0 uses them
// only for ribbon-button highlighting (no scroll yet); the attributes
// stay in place so the scroll-into-view behavior is a one-line addition.

import type { FormattingFields } from '../../data/onboard-v2-fields'
import type { TableStylePreset } from '../../types'
import type { DocSample } from '../../data/prov2-samples'

interface DocumentSampleV2Props {
  sample: DocSample
  state: FormattingFields
  tableStyle: TableStylePreset
  /** Active pack toggles affecting the preview body (cover-letter cue). */
  packsActive: { coverLetter?: boolean }
}

export function DocumentSampleV2({
  sample,
  state,
  tableStyle,
  packsActive,
}: DocumentSampleV2Props) {
  const headingFont = String(state['headings.font'])
  const h1Size = Number(state['headings.sizes.1'])
  const h1Bold = Boolean(state['headings.bold.1'])
  const h1Color = String(state['headings.colors.1'])
  const h2Size = Number(state['headings.sizes.2'])
  const h2Bold = Boolean(state['headings.bold.2'])
  const h2Color = String(state['headings.colors.2'])

  const bodyFont = String(state['body.font'])
  const bodySize = Number(state['body.size'])
  const lineSpacing = Number(state['body.lineSpacing'])
  const bodySpaceAfter = Number(state['body.spaceAfter'])

  const figCapFont = String(state['figureCaption.font'])
  const figCapSize = Number(state['figureCaption.size'])
  const figCapItalic = Boolean(state['figureCaption.italic'])
  const figCapColor = String(state['figureCaption.color'])

  const tblCapFont = String(state['tableCaption.font'])
  const tblCapSize = Number(state['tableCaption.size'])
  const tblCapItalic = Boolean(state['tableCaption.italic'])
  const tblCapColor = String(state['tableCaption.color'])

  const tableHeaderBg = tableStyle.headerBg ?? '#E7E6E6'
  const tableHeaderText = tableStyle.headerTextColor ?? '#333333'
  const tableHeaderFont = tableStyle.headerFont ?? 'Calibri'
  const tableHeaderBold = Boolean(tableStyle.headerBold)
  const tableAltBg = tableStyle.altRowBg ?? '#FFFFFF'
  const tableCellBg = tableStyle.defaultCellBg ?? '#FFFFFF'
  const tableBorderColor = tableStyle.borderColor ?? '#999999'
  const tableBorderWidth = Number(tableStyle.borderWidth ?? 0.5)
  const tableFont = String(tableStyle.bodyFont ?? 'Calibri')
  const tableFontSize = Number(tableStyle.bodyFontSize ?? 10)
  const tableTextColor = tableStyle.bodyTextColor ?? '#333333'

  return (
    <div
      className="bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.06)] mx-auto"
      style={{ width: 'min(720px, 100%)', padding: '40px 56px' }}
    >
      <h1
        data-pv="headings"
        style={{
          fontFamily: `"${headingFont}", "Calibri Light", sans-serif`,
          fontSize: `${h1Size}px`,
          fontWeight: h1Bold ? 700 : 400,
          color: h1Color,
          margin: '0 0 14px',
          lineHeight: 1.2,
        }}
      >
        {sample.h1}
      </h1>

      <h2
        data-pv="headings"
        style={{
          fontFamily: `"${headingFont}", "Calibri Light", sans-serif`,
          fontSize: `${h2Size}px`,
          fontWeight: h2Bold ? 700 : 400,
          color: h2Color,
          margin: '12px 0 8px',
          lineHeight: 1.25,
        }}
      >
        {sample.h2a}
      </h2>
      <p
        data-pv="body"
        style={{
          fontFamily: `"${bodyFont}", "Calibri", sans-serif`,
          fontSize: `${bodySize}px`,
          lineHeight: lineSpacing,
          marginBottom: `${bodySpaceAfter}px`,
          color: '#221610',
        }}
      >
        {sample.bodyA}
      </p>

      <h2
        data-pv="headings"
        style={{
          fontFamily: `"${headingFont}", "Calibri Light", sans-serif`,
          fontSize: `${h2Size}px`,
          fontWeight: h2Bold ? 700 : 400,
          color: h2Color,
          margin: '16px 0 8px',
          lineHeight: 1.25,
        }}
      >
        {sample.h2b}
      </h2>
      <p
        data-pv="body"
        style={{
          fontFamily: `"${bodyFont}", "Calibri", sans-serif`,
          fontSize: `${bodySize}px`,
          lineHeight: lineSpacing,
          marginBottom: `${bodySpaceAfter}px`,
          color: '#221610',
        }}
      >
        {sample.bodyB}
      </p>

      {/* Bulleted list — bullet style + indent are display-only in v1 */}
      <ul
        data-pv="lists"
        style={{
          fontFamily: `"${bodyFont}", "Calibri", sans-serif`,
          fontSize: `${bodySize}px`,
          lineHeight: lineSpacing,
          margin: `0 0 ${bodySpaceAfter}px`,
          paddingLeft: '20px',
          color: '#221610',
          listStyleType: 'disc',
        }}
      >
        {sample.bullets.map((b, i) => (
          <li key={i} style={{ marginBottom: '2px' }}>
            {b}
          </li>
        ))}
      </ul>

      {/* Figure placeholder + caption (figure caption styling) */}
      <div data-pv="captions" style={{ margin: '14px 0' }}>
        <div
          aria-hidden
          style={{
            background: '#F1F0EE',
            border: '1px dashed #C9C5BF',
            color: '#94908A',
            fontSize: '10px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '24px 0',
            textAlign: 'center',
          }}
        >
          Figure placeholder
        </div>
        <div
          style={{
            fontFamily: `"${figCapFont}", sans-serif`,
            fontSize: `${figCapSize}px`,
            fontStyle: figCapItalic ? 'italic' : 'normal',
            color: figCapColor,
            marginTop: '6px',
          }}
        >
          {sample.figureCaption}
        </div>
      </div>

      {/* Table caption + table (table caption styling, table preset) */}
      <div data-pv="tables" style={{ margin: '16px 0' }}>
        <div
          style={{
            fontFamily: `"${tblCapFont}", sans-serif`,
            fontSize: `${tblCapSize}px`,
            fontStyle: tblCapItalic ? 'italic' : 'normal',
            color: tblCapColor,
            marginBottom: '6px',
          }}
        >
          {sample.tableCaption}
        </div>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: `"${tableFont}", "Calibri", sans-serif`,
            fontSize: `${tableFontSize}px`,
            color: tableTextColor,
          }}
        >
          <thead>
            <tr style={{ background: tableHeaderBg }}>
              {sample.tableHeader.map((h, i) => (
                <th
                  key={i}
                  style={{
                    color: tableHeaderText,
                    fontFamily: `"${tableHeaderFont}", "Calibri", sans-serif`,
                    fontWeight: tableHeaderBold ? 700 : 500,
                    textAlign: 'left',
                    padding: '6px 8px',
                    border: `${tableBorderWidth}px solid ${tableBorderColor}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sample.tableRows.map((row, ri) => {
              const bg = ri % 2 === 1 ? tableAltBg : tableCellBg
              return (
                <tr key={ri} style={{ background: bg }}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      style={{
                        padding: '6px 8px',
                        border: `${tableBorderWidth}px solid ${tableBorderColor}`,
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pack overlay — Cover letter validation cue. Inline so the user
          sees the *effect* of toggling the pack on, without any modal
          or sidebar. v0 surfaces only the cover-letter pack since the
          other PRO toggles are display-only with no backing rules. */}
      {packsActive.coverLetter ? (
        <div
          data-pv="packs"
          style={{
            background: '#FFF7ED',
            border: '1px solid #FED7AA',
            borderLeft: '3px solid #EC5B13',
            color: '#221610',
            padding: '8px 10px',
            marginTop: '12px',
            fontSize: '11px',
            lineHeight: 1.4,
          }}
        >
          <div style={{ fontWeight: 600, color: '#EC5B13' }}>
            Cover letter validation
          </div>
          <div style={{ marginTop: '2px', color: '#5C4A3F' }}>
            Required: regulatory contact · technical contact · submission size · virus-free attestation
          </div>
        </div>
      ) : null}
    </div>
  )
}
